import {
  Box,
  Button,
  ButtonProps,
  chakra,
  IconButton,
  Input,
  InputGroup,
  NativeSelect,
  Switch,
  Field as ChakraField,
} from '@chakra-ui/react'
import { ViewIcon, ViewOffIcon } from '@src/components/sco/icons'
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  createContext,
  useContext,
  ReactNode,
} from 'react'

export const hydrateFormValues = values => {
  const hydratedValues: any = {}
  Object.keys(values).forEach(key => {
    if (values[key]) {
      hydratedValues[key] = values[key]
    } else {
      hydratedValues[key] = ''
    }
  })
  return hydratedValues
}

export const dehydrateFormValues = (values: any = {}) => {
  const dehydratedValues: any = {}
  Object.keys(values).forEach(key => {
    const v = values[key]
    if (v == null || v === '') {
      dehydratedValues[key] = null
    } else {
      dehydratedValues[key] = v
    }
  })
  return dehydratedValues
}

export const renderCurrency = (inputAmountInCents, pretty = false, colorize = true, decimals = 2) => {
  // For table numeric columns, wrap in <AdminNumericCell> (from Admin/shared/AdminDataTable)
  // or <Table.Cell textAlign="right" fontVariantNumeric="tabular-nums"> to ensure alignment.
  // See AdminDataTable for AdminNumericCell / AdminNumericHeader helpers.
  let amountInCents = inputAmountInCents
  if (typeof amountInCents !== 'number') {
    amountInCents = parseInt(amountInCents)
  }

  if (isNaN(amountInCents)) {
    return '-'
  }

  let color = null
  if (colorize && amountInCents !== 0) {
    color = amountInCents < 0 ? 'red.500' : 'green.500'
  }

  const amount = amountInCents / 100
  const absoluteAmount = Math.abs(amount)
  let suffix = ''
  let divisor = 1
  if (pretty) {
    if (absoluteAmount >= 1000000000) {
      divisor = 1000000000
      suffix = 'B'
    } else if (absoluteAmount >= 1000000) {
      divisor = 1000000
      suffix = 'M'
    } else if (absoluteAmount >= 100000) {
      divisor = 1000
      suffix = 'K'
    }
  }

  const localString = (amount / divisor).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })

  return (
    <chakra.span color={color} style={{ textWrap: 'nowrap' }} title={`${amount}`}>
      {decimals == 0 ? localString.replaceAll('.00', '') : localString}
      {suffix}
    </chakra.span>
  )
}

export const renderPaymentDescription = (
  description,
  showFullDescription = false,
  setShowFullDescription = null
) => {
  const parts = (description || '[No Description]').split(/\;/).filter(Boolean)

  return (
    <chakra.strong
      cursor={'pointer'}
      display={'block'}
      title={description}
      onClick={() => (setShowFullDescription ? setShowFullDescription(!showFullDescription) : null)}
    >
      {showFullDescription ? <chakra.span>{description}</chakra.span> : <>{parts.pop()}</>}
    </chakra.strong>
  )
}

// --- Saas UI Form replacements (to remove @saas-ui/* dependency) ---

export type FieldProps = {
  name: string
  label?: string
  type?: 'text' | 'native-select' | string
  placeholder?: string
  options?: Array<{ label: string; value: string | number }>
  [key: string]: any
}

interface SaasFormContextValue {
  values: Record<string, any>
  setFieldValue: (name: string, value: any) => void
  reset: () => void
}

const SaasFormContext = createContext<SaasFormContextValue | null>(null)

// Stable Field component (defined at module scope so its identity never changes).
// This is the key fix: previously Field was recreated inside every Form render.
export const Field: React.FC<FieldProps> = fieldProps => {
  const ctx = useContext(SaasFormContext)
  if (!ctx) {
    return null
  }

  const { values: ctxValues, setFieldValue } = ctx
  const val = ctxValues[fieldProps.name] ?? ''

  const handleFieldChange = (e: any) => {
    let newVal: any
    if (e && e.target) {
      newVal = e.target.value
    } else {
      newVal = e
    }
    setFieldValue(fieldProps.name, newVal)
  }

  const commonProps = {
    name: fieldProps.name,
    value: val,
    onChange: handleFieldChange,
    placeholder: fieldProps.placeholder,
    size: 'sm',
  }

  let inputEl: ReactNode
  if (fieldProps.type === 'native-select' || fieldProps.type === 'select') {
    // size lives on NativeSelect.Root (recipe variant); on NativeSelect.Field it
    // would pass through as the native <select size> attribute instead
    const { size: _size, ...selectFieldProps } = commonProps
    inputEl = (
      <NativeSelect.Root size={'sm'}>
        <NativeSelect.Field {...selectFieldProps}>
          {(fieldProps.options || []).map(opt => (
            <option key={String(opt.value)} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
    )
  } else if (fieldProps.type === 'switch') {
    // Use onCheckedChange per Chakra v3; value is boolean.
    // Use the compound Switch.Root (consistent with chakra-pro patterns in the project).
    inputEl = (
      <Switch.Root
        checked={!!val}
        size={'sm'}
        onCheckedChange={({ checked }) => setFieldValue(fieldProps.name, !!checked)}
      />
    )
  } else {
    inputEl = (
      <Input {...commonProps} type={fieldProps.type === 'text' ? 'text' : fieldProps.type || 'text'} />
    )
  }

  return (
    <ChakraField.Root>
      {fieldProps.label && (
        <ChakraField.Label fontSize={'0.85em'} lineHeight={1} mb={1}>
          {fieldProps.label}
        </ChakraField.Label>
      )}
      {inputEl}
    </ChakraField.Root>
  )
}

export const Form = ({
  defaultValues = {},
  onChange,
  onSubmit,
  children,
  ...rest
}: {
  defaultValues?: Record<string, any>
  onChange?: (values: Record<string, any>) => void
  onSubmit?: (values: Record<string, any>) => void | Promise<void>
  children?: ReactNode | ((ctx: { Field: React.FC<FieldProps>; reset: () => void }) => ReactNode)
  [key: string]: any
}) => {
  const [values, setValues] = useState<Record<string, any>>(() => ({ ...defaultValues }))
  const valuesRef = useRef(values)
  valuesRef.current = values

  // Store latest onChange in a ref so the updater functions we put in context
  // have a completely stable identity (onChange is usually an inline arrow in callers).
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // Keep a stable reference to the previous defaultValues for comparison
  const defaultValuesRef = useRef(defaultValues)

  // Adopt new defaultValues when the parent passes updated ones
  // (e.g. URL query params changed and the filter component received new props).
  // This also makes the form fields reflect the currently applied filters after navigation.
  useEffect(() => {
    const prev = defaultValuesRef.current
    // Cheap but effective deep compare for these small primitive-only filter objects
    if (JSON.stringify(defaultValues) !== JSON.stringify(prev)) {
      const next = { ...defaultValues }
      valuesRef.current = next
      setValues(next)
      defaultValuesRef.current = defaultValues
    }
  }, [defaultValues])

  const setFieldValue = useCallback((name: string, val: any) => {
    const next = { ...valuesRef.current, [name]: val }
    valuesRef.current = next
    setValues(next)
    onChangeRef.current?.(next)
  }, [])

  const reset = useCallback(() => {
    const resetVals: Record<string, any> = {}
    Object.keys(valuesRef.current).forEach(key => {
      resetVals[key] = ''
    })
    valuesRef.current = resetVals
    setValues(resetVals)
    onChangeRef.current?.(resetVals)
  }, [])

  const ctxValue = useMemo(() => ({ values, setFieldValue, reset }), [values, setFieldValue, reset])

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault()
    }
    onSubmit?.(values)
  }

  return (
    <chakra.form onSubmit={handleSubmit} {...rest}>
      <SaasFormContext.Provider value={ctxValue}>
        {typeof children === 'function' ? children({ Field, reset }) : children}
      </SaasFormContext.Provider>
    </chakra.form>
  )
}

export const FormLayout = ({ children, ...props }: { children?: ReactNode; [key: string]: any }) => (
  <Box {...props}>{children}</Box>
)

export const SubmitButton = (props: ButtonProps) => (
  <Button colorPalette={'gray'} type={'submit'} {...props} />
)

export const PasswordInput = (props: any) => {
  const [show, setShow] = useState(false)
  const { size = 'sm', type: forcedType, ...inputProps } = props
  const inputType = show ? 'text' : forcedType === 'text' ? 'text' : 'password'

  return (
    <InputGroup
      size={size}
      endElement={
        <IconButton
          aria-label={show ? 'Hide password' : 'Show password'}
          size={'xs'}
          tabIndex={-1}
          type={'button'}
          variant={'ghost'}
          onClick={() => setShow(!show)}
        >
          {show ? <ViewOffIcon /> : <ViewIcon />}
        </IconButton>
      }
    >
      <Input size={size} type={inputType} {...inputProps} pr={'2.5rem'} />
    </InputGroup>
  )
}
